/**
 * Medplum / FHIR data-access layer for notification templates.
 *
 * Templates are persisted as ActivityDefinition resources with the full
 * NotificationTemplateConfig stored in a JSON-valued extension.
 */
import type { MedplumClient } from '@medplum/core';
import type { ActivityDefinition, Extension } from '@medplum/fhirtypes';

import type {
  NotificationTemplateConfig,
  NotificationTemplateDraft,
  NotificationTemplateListItem,
  NotificationTemplateStatus,
} from './notification-template.types';
import {
  NOTIFICATION_AD_URL_PREFIX,
  NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
  STATUS_FROM_FHIR,
  STATUS_TO_FHIR,
} from './notification-template.constants';

// ---------------------------------------------------------------------------
// Extension helpers
// ---------------------------------------------------------------------------

export function getJsonExtension<T>(
  resource: { extension?: Extension[] } | undefined,
  url: string,
): T | undefined {
  if (!resource?.extension) return undefined;
  const ext = resource.extension.find((e) => e.url === url);
  if (!ext) return undefined;
  const raw = (ext as unknown as Record<string, unknown>).valueString as string | undefined;
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

export function setJsonExtension<T>(
  resource: { extension?: Extension[] },
  url: string,
  value: T,
): void {
  const extensions = resource.extension ?? [];
  const existing = extensions.findIndex((e) => e.url === url);
  const ext: Extension = { url, valueString: JSON.stringify(value) } as Extension;
  if (existing >= 0) {
    extensions[existing] = ext;
  } else {
    extensions.push(ext);
  }
  resource.extension = extensions;
}

// ---------------------------------------------------------------------------
// Mapping: Draft ↔ ActivityDefinition
// ---------------------------------------------------------------------------

export function draftToActivityDefinition(
  draft: NotificationTemplateDraft,
): ActivityDefinition {
  const fhirStatus = STATUS_TO_FHIR[draft.status] ?? 'draft';

  const config: NotificationTemplateConfig = {
    id: draft.id ?? '',
    version: draft.version,
    status: draft.status,
    name: draft.name,
    description: draft.description || undefined,
    channel: draft.channel,
    from: draft.from,
    subject: draft.subject,
    htmlBody: draft.htmlBody,
    textBody: draft.textBody || undefined,
    variables: draft.variables,
    updatedAt: new Date().toISOString(),
  };

  const ad: ActivityDefinition = {
    resourceType: 'ActivityDefinition',
    title: draft.name,
    description: draft.description || undefined,
    status: fhirStatus,
    version: draft.version,
    url: `${NOTIFICATION_AD_URL_PREFIX}${draft.id ?? 'new'}`,
    extension: [],
  };

  if (draft.id) {
    (ad as { id?: string }).id = draft.id;
  }

  setJsonExtension(ad, NOTIFICATION_TEMPLATE_CONFIG_EXTENSION, config);

  return ad;
}

export function activityDefinitionToDraft(
  ad: ActivityDefinition,
): NotificationTemplateDraft {
  const config = getJsonExtension<NotificationTemplateConfig & { automation?: unknown }>(
    ad,
    NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
  );

  const status: NotificationTemplateStatus =
    (STATUS_FROM_FHIR[ad.status ?? 'draft'] as NotificationTemplateStatus) ?? 'draft';

  if (config) {
    return {
      id: ad.id,
      name: config.name || ad.title || '',
      description: config.description || ad.description || '',
      status: config.status || status,
      version: config.version || ad.version || '1',
      channel: config.channel || 'email',
      from: config.from || { mode: 'static', email: '', name: '' },
      subject: config.subject || '',
      htmlBody: config.htmlBody || '',
      textBody: config.textBody || '',
      variables: config.variables || [],
    };
  }

  return {
    id: ad.id,
    name: ad.title || '',
    description: ad.description || '',
    status,
    version: ad.version || '1',
    channel: 'email',
    from: { mode: 'static', email: '', name: '' },
    subject: '',
    htmlBody: '',
    textBody: '',
    variables: [],
  };
}

export function activityDefinitionToListItem(
  ad: ActivityDefinition,
): NotificationTemplateListItem {
  const config = getJsonExtension<NotificationTemplateConfig>(
    ad,
    NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
  );

  return {
    id: ad.id ?? '',
    name: config?.name || ad.title || 'Untitled',
    status:
      (STATUS_FROM_FHIR[ad.status ?? 'draft'] as NotificationTemplateStatus) ?? 'draft',
    subject: config?.subject || '',
    updatedAt: ad.meta?.lastUpdated ?? config?.updatedAt ?? '',
  };
}

// ---------------------------------------------------------------------------
// CRUD operations
// ---------------------------------------------------------------------------

export async function listNotificationTemplates(
  medplum: MedplumClient,
): Promise<ActivityDefinition[]> {
  try {
    const bundle = await medplum.search('ActivityDefinition', {
      _sort: '-_lastUpdated',
      _count: '200',
    });
    return (bundle.entry ?? [])
      .map((e) => e.resource as ActivityDefinition | undefined)
      .filter((r): r is ActivityDefinition =>
        Boolean(r?.url?.startsWith(NOTIFICATION_AD_URL_PREFIX)),
      );
  } catch {
    return [];
  }
}

export async function getNotificationTemplate(
  medplum: MedplumClient,
  id: string,
): Promise<ActivityDefinition | undefined> {
  try {
    return await medplum.readResource('ActivityDefinition', id);
  } catch {
    return undefined;
  }
}

export async function createNotificationTemplate(
  medplum: MedplumClient,
  draft: NotificationTemplateDraft,
): Promise<ActivityDefinition> {
  const ad = draftToActivityDefinition({ ...draft, id: undefined });
  const created = await medplum.createResource<ActivityDefinition>(ad);
  const updated = {
    ...created,
    url: `${NOTIFICATION_AD_URL_PREFIX}${created.id}`,
  };
  setJsonExtension(
    updated,
    NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
    {
      ...getJsonExtension<NotificationTemplateConfig>(created, NOTIFICATION_TEMPLATE_CONFIG_EXTENSION),
      id: created.id,
      createdAt: new Date().toISOString(),
    },
  );
  return medplum.updateResource<ActivityDefinition>(updated);
}

export async function updateNotificationTemplate(
  medplum: MedplumClient,
  draft: NotificationTemplateDraft,
): Promise<ActivityDefinition> {
  if (!draft.id) throw new Error('Cannot update template without an ID');
  const ad = draftToActivityDefinition(draft);
  return medplum.updateResource<ActivityDefinition>(ad);
}

export async function archiveNotificationTemplate(
  medplum: MedplumClient,
  id: string,
): Promise<ActivityDefinition> {
  const existing = await medplum.readResource('ActivityDefinition', id);
  const updated: ActivityDefinition = {
    ...existing,
    status: 'retired',
  };
  const config = getJsonExtension<NotificationTemplateConfig>(
    existing,
    NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
  );
  if (config) {
    setJsonExtension(updated, NOTIFICATION_TEMPLATE_CONFIG_EXTENSION, {
      ...config,
      status: 'archived' as const,
      updatedAt: new Date().toISOString(),
    });
  }
  return medplum.updateResource<ActivityDefinition>(updated);
}

// ---------------------------------------------------------------------------
// Archive protection: find active rules referencing a template
// ---------------------------------------------------------------------------

export interface ActiveRuleReference {
  ruleId: string;
  ruleTitle: string;
}

/**
 * Search active PlanDefinitions (Rules Engine rules) for references to a
 * template ID in their action definitions. Returns the referencing rules.
 */
export async function findActiveRulesReferencingEmailTemplate(
  medplum: MedplumClient,
  templateId: string,
): Promise<ActiveRuleReference[]> {
  try {
    const bundle = await medplum.search('PlanDefinition', {
      status: 'active',
      _count: '200',
    });
    const plans = (bundle.entry ?? [])
      .map((e) => e.resource)
      .filter(Boolean) as import('@medplum/fhirtypes').PlanDefinition[];

    const refs: ActiveRuleReference[] = [];
    for (const pd of plans) {
      const raw = JSON.stringify(pd);
      if (raw.includes(templateId)) {
        refs.push({ ruleId: pd.id ?? '', ruleTitle: pd.title ?? pd.name ?? 'Untitled rule' });
      }
    }
    return refs;
  } catch {
    return [];
  }
}
